import {
  AttendanceCustomTimeRangeDayEnumValues,
  AttendanceShiftLocationTypeEnumValues,
  ContractsValueObjectsAdditionalCompensationRecurrenceEnumValues,
} from 'generated/resources/datatable.graphql'
import type {
  AttendanceCustomTimeRangeDayEnum,
  AttendanceShiftLocationTypeEnum,
  ContractsValueObjectsAdditionalCompensationRecurrenceEnum,
} from 'generated/resources/graphql'
import {
  FIXED_AMOUNT_STRATEGY,
  PER_WORKED_DAY_STRATEGY,
  VARIABLE_AMOUNT_STRATEGY,
} from 'modules/contracts/lib/additionalCompensationStrategies'

const asNonEmptyEnumValues = <T extends string>(values: readonly T[]): [T, ...T[]] => {
  if (values.length === 0) {
    throw new Error('Expected at least one enum value')
  }

  const [firstValue, ...remainingValues] = values

  if (firstValue === undefined) {
    throw new Error('Expected at least one enum value')
  }

  return [firstValue, ...remainingValues]
}

const AMOUNT_STRATEGY_TYPE_VALUES = asNonEmptyEnumValues([
  FIXED_AMOUNT_STRATEGY,
  VARIABLE_AMOUNT_STRATEGY,
  PER_WORKED_DAY_STRATEGY,
])

const getRecurrenceOrder = (value: ContractsValueObjectsAdditionalCompensationRecurrenceEnum) => {
  if (value === 'monthly') {
    return 0
  }

  const count = Number(value.match(/\d+/)?.[0])

  return Number.isNaN(count) ? Number.POSITIVE_INFINITY : count
}

const RECURRENCE_VALUES = asNonEmptyEnumValues(
  [...ContractsValueObjectsAdditionalCompensationRecurrenceEnumValues].sort(
    (left, right) => getRecurrenceOrder(left) - getRecurrenceOrder(right)
  )
)

const PER_WORKED_DAY_DAYS_COUNTED_FROM_VALUES = ['previous_month'] as const
const DEFAULT_PER_WORKED_DAY_DAYS_COUNTED_FROM = PER_WORKED_DAY_DAYS_COUNTED_FROM_VALUES[0]
const PER_WORKED_DAY_WORK_LOCATION_ORDER: Record<AttendanceShiftLocationTypeEnum, number> = {
  office: 0,
  work_from_home: 1,
  business_trip: 2,
}

const PER_WORKED_DAY_WORK_LOCATION_VALUES: AttendanceShiftLocationTypeEnum[] = [
  ...AttendanceShiftLocationTypeEnumValues,
].sort(
  (left, right) =>
    (PER_WORKED_DAY_WORK_LOCATION_ORDER[left] ?? Number.POSITIVE_INFINITY) -
    (PER_WORKED_DAY_WORK_LOCATION_ORDER[right] ?? Number.POSITIVE_INFINITY)
)

const PER_WORKED_DAY_ELIGIBLE_DAY_ORDER: Record<AttendanceCustomTimeRangeDayEnum, number> = {
  monday: 0,
  tuesday: 1,
  wednesday: 2,
  thursday: 3,
  friday: 4,
  saturday: 5,
  sunday: 6,
  bank_holiday: 7,
}

const PER_WORKED_DAY_ELIGIBLE_DAY_VALUES: AttendanceCustomTimeRangeDayEnum[] = [
  ...AttendanceCustomTimeRangeDayEnumValues,
].sort(
  (left, right) =>
    (PER_WORKED_DAY_ELIGIBLE_DAY_ORDER[left] ?? Number.POSITIVE_INFINITY) -
    (PER_WORKED_DAY_ELIGIBLE_DAY_ORDER[right] ?? Number.POSITIVE_INFINITY)
)

const PER_WORKED_DAY_CALCULATION_SOURCE = 'time_tracking'
const PER_WORKED_DAY_REFERENCE_TIME = 'past_period'

type AdditionalCompensationWizardAmountStrategyType =
  | typeof FIXED_AMOUNT_STRATEGY
  | typeof VARIABLE_AMOUNT_STRATEGY
  | typeof PER_WORKED_DAY_STRATEGY

type BasicContext = {
  amountStrategyType?: AdditionalCompensationWizardAmountStrategyType
  payrollConceptId?: string
}

type BasicStepValues = {
  payrollConceptId: string
  amountStrategyType: AdditionalCompensationWizardAmountStrategyType
  recurrence: ContractsValueObjectsAdditionalCompensationRecurrenceEnum
  firstPaymentOn: Date
}

type PerWorkedDayDefinitionValues = {
  amountPerDay?: number | null | undefined
  daysCountedFrom?: (typeof PER_WORKED_DAY_DAYS_COUNTED_FROM_VALUES)[number] | undefined
  minimumHoursPerDay?: number | null | undefined
  workLocations?: string[] | undefined
  includedAbsenceLeaveTypeIds?: string[] | undefined
  eligibleDays?: string[] | undefined
}

type DefinitionStepValues = {
  fixedAmount?: number | null | undefined
  maximumAmountPerCycle?: number | null | undefined
  perWorkedDayFields?: PerWorkedDayDefinitionValues | undefined
}

type RawDefinitionStepValues = {
  fixedAmount?: number | null | undefined
  maximumAmountPerCycle?: number | null | undefined
  perWorkedDayAmountPerDay?: number | null | undefined
  perWorkedDayDaysCountHeader?: string | undefined
  perWorkedDayDaysCountedFrom?: (typeof PER_WORKED_DAY_DAYS_COUNTED_FROM_VALUES)[number] | undefined
  perWorkedDayMinimumHoursPerDay?: number | null | undefined
  perWorkedDayWorkLocations?: string[] | undefined
  perWorkedDayIncludedAbsenceLeaveTypeIds?: string[] | undefined
  perWorkedDayEligibleDays?: string[] | undefined
}

type NormalizableDefinitionStepValues = Omit<
  RawDefinitionStepValues,
  | 'fixedAmount'
  | 'maximumAmountPerCycle'
  | 'perWorkedDayAmountPerDay'
  | 'perWorkedDayMinimumHoursPerDay'
> & {
  fixedAmount?: number | string | null | undefined
  maximumAmountPerCycle?: number | string | null | undefined
  perWorkedDayAmountPerDay?: number | string | null | undefined
  perWorkedDayMinimumHoursPerDay?: number | string | null | undefined
}

type AdditionalCompensationWizardInitialValues = {
  basic?: Partial<BasicStepValues>
  definition?: Partial<RawDefinitionStepValues>
}

type RawPerWorkedDayDefaultFields = Pick<
  RawDefinitionStepValues,
  | 'perWorkedDayDaysCountedFrom'
  | 'perWorkedDayWorkLocations'
  | 'perWorkedDayIncludedAbsenceLeaveTypeIds'
  | 'perWorkedDayEligibleDays'
>

const withPerWorkedDayDefinitionDefaults = (
  definition: Partial<RawDefinitionStepValues> | undefined
): Partial<RawDefinitionStepValues> & RawPerWorkedDayDefaultFields => {
  const perWorkedDayDefaults = getDefaultPerWorkedDayFields()

  return {
    ...definition,
    perWorkedDayDaysCountedFrom:
      definition?.perWorkedDayDaysCountedFrom ?? perWorkedDayDefaults.daysCountedFrom,
    perWorkedDayWorkLocations:
      definition?.perWorkedDayWorkLocations ?? perWorkedDayDefaults.workLocations,
    perWorkedDayIncludedAbsenceLeaveTypeIds:
      definition?.perWorkedDayIncludedAbsenceLeaveTypeIds ?? [],
    perWorkedDayEligibleDays: definition?.perWorkedDayEligibleDays ?? [],
  }
}

const getDefaultPerWorkedDayFields = (): {
  daysCountedFrom: (typeof PER_WORKED_DAY_DAYS_COUNTED_FROM_VALUES)[number]
  workLocations: AttendanceShiftLocationTypeEnum[]
  includedAbsenceLeaveTypeIds: string[]
  eligibleDays: AttendanceCustomTimeRangeDayEnum[]
} => ({
  daysCountedFrom: DEFAULT_PER_WORKED_DAY_DAYS_COUNTED_FROM,
  workLocations: [...PER_WORKED_DAY_WORK_LOCATION_VALUES],
  includedAbsenceLeaveTypeIds: [],
  eligibleDays: [],
})

const normalizeDefinitionNumber = (
  value: number | string | null | undefined
): number | null | undefined => {
  if (value == null || value === '') return undefined

  if (typeof value === 'string') {
    const parsedValue = Number(value)

    return Number.isNaN(parsedValue) ? undefined : parsedValue
  }

  return Number.isNaN(value) ? undefined : value
}

const DURATION_FIELDS = {
  hours: {
    maxVisibleDigits: 5,
  },
  minutes: {
    maxVisibleDigits: 10,
  },
}

const isNumber = (value: unknown): value is number => typeof value === 'number'

const isString = (value: unknown): value is string => typeof value === 'string'

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const isRecurrenceValue = (
  value: unknown
): value is ContractsValueObjectsAdditionalCompensationRecurrenceEnum =>
  isString(value) && RECURRENCE_VALUES.some((recurrence) => recurrence === value)

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every(isString)

const isPerWorkedDayDefinitionValues = (value: unknown): value is PerWorkedDayDefinitionValues =>
  isRecord(value) &&
  isOptionalNumericField(value.amountPerDay) &&
  (value.daysCountedFrom === undefined ||
    value.daysCountedFrom === DEFAULT_PER_WORKED_DAY_DAYS_COUNTED_FROM) &&
  isOptionalNumericField(value.minimumHoursPerDay) &&
  (value.workLocations === undefined || isStringArray(value.workLocations)) &&
  (value.includedAbsenceLeaveTypeIds === undefined ||
    isStringArray(value.includedAbsenceLeaveTypeIds)) &&
  (value.eligibleDays === undefined || isStringArray(value.eligibleDays))

const isAdditionalCompensationWizardAmountStrategyType = (
  value: unknown
): value is AdditionalCompensationWizardAmountStrategyType =>
  value === FIXED_AMOUNT_STRATEGY ||
  value === VARIABLE_AMOUNT_STRATEGY ||
  value === PER_WORKED_DAY_STRATEGY

const isBasicStepValues = (value: unknown): value is BasicStepValues =>
  isRecord(value) &&
  isString(value.payrollConceptId) &&
  isAdditionalCompensationWizardAmountStrategyType(value.amountStrategyType) &&
  isRecurrenceValue(value.recurrence) &&
  value.firstPaymentOn instanceof Date

const isOptionalNumericField = (value: unknown) =>
  value == null || value === '' || isNumber(value) || isString(value)

const isRawDefinitionStepValues = (value: unknown): value is RawDefinitionStepValues =>
  isRecord(value) &&
  isOptionalNumericField(value.fixedAmount) &&
  isOptionalNumericField(value.maximumAmountPerCycle) &&
  isOptionalNumericField(value.perWorkedDayAmountPerDay) &&
  (value.perWorkedDayDaysCountHeader === undefined ||
    isString(value.perWorkedDayDaysCountHeader)) &&
  (value.perWorkedDayDaysCountedFrom === undefined ||
    value.perWorkedDayDaysCountedFrom === DEFAULT_PER_WORKED_DAY_DAYS_COUNTED_FROM) &&
  isOptionalNumericField(value.perWorkedDayMinimumHoursPerDay) &&
  (value.perWorkedDayWorkLocations === undefined ||
    isStringArray(value.perWorkedDayWorkLocations)) &&
  (value.perWorkedDayIncludedAbsenceLeaveTypeIds === undefined ||
    isStringArray(value.perWorkedDayIncludedAbsenceLeaveTypeIds)) &&
  (value.perWorkedDayEligibleDays === undefined || isStringArray(value.perWorkedDayEligibleDays))

const isSupportedAmountStrategyType = (
  amountStrategyType: unknown
): amountStrategyType is AdditionalCompensationWizardAmountStrategyType =>
  amountStrategyType === FIXED_AMOUNT_STRATEGY ||
  amountStrategyType === VARIABLE_AMOUNT_STRATEGY ||
  amountStrategyType === PER_WORKED_DAY_STRATEGY

export {
  AMOUNT_STRATEGY_TYPE_VALUES,
  DURATION_FIELDS,
  DEFAULT_PER_WORKED_DAY_DAYS_COUNTED_FROM,
  FIXED_AMOUNT_STRATEGY,
  PER_WORKED_DAY_CALCULATION_SOURCE,
  PER_WORKED_DAY_DAYS_COUNTED_FROM_VALUES,
  PER_WORKED_DAY_ELIGIBLE_DAY_VALUES,
  PER_WORKED_DAY_REFERENCE_TIME,
  PER_WORKED_DAY_STRATEGY,
  PER_WORKED_DAY_WORK_LOCATION_VALUES,
  RECURRENCE_VALUES,
  VARIABLE_AMOUNT_STRATEGY,
  getDefaultPerWorkedDayFields,
  isBasicStepValues,
  isNumber,
  isPerWorkedDayDefinitionValues,
  isRawDefinitionStepValues,
  isSupportedAmountStrategyType,
  normalizeDefinitionNumber,
  withPerWorkedDayDefinitionDefaults,
}

export type {
  AdditionalCompensationWizardAmountStrategyType,
  AdditionalCompensationWizardInitialValues,
  BasicContext,
  BasicStepValues,
  DefinitionStepValues,
  NormalizableDefinitionStepValues,
  PerWorkedDayDefinitionValues,
  RawDefinitionStepValues,
}
